<template>
  <Teleport to="body">
    <div
      v-if="menuState"
      ref="panel"
      class="conference-text-menu-strong fixed z-40 min-w-48 overflow-hidden rounded-md border border-black/10 bg-white py-1 text-slate-700 shadow-[0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.08)]"
      :style="panelStyle"
      :aria-label="menuState.kind === 'participant' ? 'Participant actions' : 'Room actions'"
      role="menu"
      @contextmenu.prevent
    >
      <template v-if="menuState.kind === 'participant'">
        <template v-if="viewerIsTeacher">
          <label
            v-for="permission in permissionItems"
            :key="permission.key"
            class="flex cursor-pointer items-center gap-2 px-2.5 py-1.5 hover:bg-slate-100"
          >
            <input
              class="h-3.5 w-3.5"
              type="checkbox"
              :disabled="actionPending"
              :checked="menuState.participant.permissions[permission.key]"
              @change="setPermission(permission.key, ($event.target as HTMLInputElement).checked)"
            />
            <span class="min-w-4.5 text-center text-slate-500">{{ permission.icon }}</span>
            <span>{{ permission.label }}</span>
          </label>
        </template>

        <div v-if="viewerIsTeacher" class="my-0.5 border-t border-slate-200" />

        <button
          v-for="action in participantActions"
          :key="action.label"
          type="button"
          class="flex w-full items-center gap-2 px-2.5 py-1.5 text-left hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="actionPending || action.disabled"
          :aria-disabled="actionPending || action.disabled"
          role="menuitem"
          @click="action.run"
        >
          <span class="min-w-4.5 text-center text-slate-500">{{ action.icon }}</span>
          <span>{{ action.label }}</span>
        </button>

        <template v-if="viewerIsTeacher && moveRoomOptions.length > 0">
          <div class="my-0.5 border-t border-slate-200" />
          <button
            ref="moveRoomTrigger"
            type="button"
            class="flex w-full items-center gap-2 px-2.5 py-1.5 text-left hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="actionPending"
            :aria-disabled="actionPending"
            role="menuitem"
            @click="openMoveRoomSubmenu()"
            @mouseenter="openMoveRoomSubmenu()"
            @focus="openMoveRoomSubmenu()"
          >
            <span class="min-w-4.5 text-center text-slate-500">↪</span>
            <span>Move to room</span>
            <span class="ml-auto text-slate-400">›</span>
          </button>
        </template>

        <div v-if="viewerIsTeacher" class="my-0.5 border-t border-slate-200" />

        <div v-for="item in participantInfoRows" :key="item.label" class="flex items-center gap-2 px-2.5 py-1.5">
          <span class="min-w-4.5 text-center text-slate-500">{{ item.icon }}</span>
          <span>{{ item.label }}</span>
        </div>
      </template>

      <template v-else>
        <button
          v-for="action in roomActions"
          :key="action.label"
          type="button"
          class="flex w-full items-center gap-2 px-2.5 py-1.5 text-left hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="actionPending || action.disabled"
          :aria-disabled="actionPending || action.disabled"
          role="menuitem"
          @click="action.run"
        >
          <span class="min-w-4.5 text-center text-slate-500">{{ action.icon }}</span>
          <span>{{ action.label }}</span>
        </button>
      </template>
    </div>

    <div
      v-if="menuState?.kind === 'participant' && viewerIsTeacher && moveRoomOptions.length > 0 && moveRoomSubmenuOpen"
      ref="submenuPanel"
      class="conference-text-menu-strong fixed z-50 min-w-44 overflow-hidden rounded-md border border-black/10 bg-white py-1 text-slate-700 shadow-[0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.08)]"
      :style="submenuStyle"
      aria-label="Move participants to room"
      role="menu"
      @contextmenu.prevent
      @mouseenter="openMoveRoomSubmenu()"
    >
      <button
        v-for="room in moveRoomOptions"
        :key="room.slug"
        :data-testid="`move-participants-${room.slug}`"
        type="button"
        class="flex w-full items-center gap-2 px-2.5 py-1.5 text-left hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="actionPending"
        :aria-disabled="actionPending"
        role="menuitem"
        @click="void moveSelectedParticipantsToRoom(room.slug)"
      >
        <span class="min-w-4.5 text-center text-slate-500">↪</span>
        <span>{{ room.displayName }}</span>
      </button>
    </div>
  </Teleport>

  <CDialog v-model:open="showRenameRoomDialog" hide-header panel-test-id="conference-rename-room-dialog" width="md">
    <label class="flex flex-col gap-1">
      <span class="conference-text-caption font-semibold tracking-[0.2em] text-slate-500 uppercase">Rename Room To:</span>
      <CInput v-model="renameRoomDraft" data-autofocus size="sm" />
    </label>
    <p v-if="renameRoomError" class="mt-2 text-sm font-medium text-rose-700" data-testid="rename-room-error" role="alert">
      {{ renameRoomError }}
    </p>

    <template #footer="{ close }">
      <div class="flex justify-end gap-1.5">
        <CButton size="sm" tone="chrome" :disabled="actionPending" @click="close">Cancel</CButton>
        <CButton size="sm" tone="accent" :disabled="!canCommitRenameRoom" @click="commitRenameRoom(close)">OK</CButton>
      </div>
    </template>
  </CDialog>

  <CDialog
    v-model:open="showKickDialog"
    description="Choose how long the kicked participant should wait before they can manually rejoin."
    panel-test-id="conference-kick-dialog"
    title="Kick Participant"
    width="md"
  >
    <div class="flex flex-col gap-3">
      <p class="text-sm text-slate-700">{{ kickDialogNames }} will be kicked.</p>

      <div class="grid gap-2">
        <label
          v-for="option in kickDurationOptions"
          :key="option.value"
          class="flex cursor-pointer items-start gap-2 rounded-md border border-slate-200 px-3 py-2 hover:border-slate-300"
        >
          <input
            :checked="kickDurationPreset === option.value"
            class="mt-0.5 h-4 w-4"
            name="kick-duration"
            type="radio"
            @change="kickDurationPreset = option.value"
          />

          <span class="flex flex-col gap-0.5">
            <span class="text-sm font-medium text-slate-900">{{ option.label }}</span>
            <span class="text-xs text-slate-500">{{ option.description }}</span>
          </span>
        </label>
      </div>

      <label v-if="kickDurationPreset === 'custom'" class="flex flex-col gap-1">
        <span class="conference-text-caption font-semibold tracking-[0.2em] text-slate-500 uppercase">Custom Minutes</span>
        <CInput v-model="customKickDurationMinutes" data-autofocus size="sm" type="number" />
      </label>

      <p v-if="kickDurationError" class="text-sm font-medium text-rose-700" role="alert">
        {{ kickDurationError }}
      </p>
    </div>

    <template #footer="{ close }">
      <div class="flex justify-end gap-1.5">
        <CButton size="sm" tone="chrome" :disabled="actionPending" @click="close">Cancel</CButton>
        <CButton size="sm" tone="danger" :disabled="!canCommitKick" @click="commitKick(close)">Kick</CButton>
      </div>
    </template>
  </CDialog>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, type CSSProperties } from 'vue';
import { useConvexClient, useConvexMutation } from 'convex-vue';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import type { ConferenceParticipant, ConferenceRoom } from '../convex/useConference';
import { getPermissionLabel } from '../features/permissions';
import CButton from './primitives/CButton.vue';
import CDialog from './primitives/CDialog.vue';
import CInput from './primitives/CInput.vue';

type ParticipantDetail = {
  awayTime: number;
  awayTimeStamp: number;
  browserName: string;
  browserVersion: string;
  kickedCount: number;
  participantId: string;
};

type KickDurationPreset = '0' | '1' | '5' | '10' | 'custom';

type ParticipantMenuState = {
  kind: 'participant';
  participant: ConferenceParticipant;
  participants: ConferenceParticipant[];
  x: number;
  y: number;
};

type RoomMenuRoom = ConferenceRoom & {
  participants: ConferenceParticipant[];
};

type RoomMenuState = {
  kind: 'room';
  room: RoomMenuRoom;
  x: number;
  y: number;
};

const props = defineProps<{
  chatEnabled: boolean;
  conferenceSlug: string;
  menuState: ParticipantMenuState | RoomMenuState | null;
  rooms: ConferenceRoom[];
  viewerId: Id<'participants'>;
  viewerRole: 'student' | 'teacher' | 'recorder';
}>();

const emit = defineEmits<{
  (event: 'close'): void;
  (event: 'notice', payload: { detail?: string; label: string }): void;
  (event: 'open-thread', participantId: Id<'participants'>): void;
}>();

const panel = ref<HTMLElement | null>(null);
const panelStyle = ref<CSSProperties>({});
const submenuPanel = ref<HTMLElement | null>(null);
const submenuStyle = ref<CSSProperties>({});
const moveRoomTrigger = ref<HTMLElement | null>(null);
const kickDialogParticipants = ref<ConferenceParticipant[]>([]);
const renameRoomTarget = ref<{ displayName: string; slug: string } | null>(null);
const showKickDialog = ref(false);
const showRenameRoomDialog = ref(false);
const customKickDurationMinutes = ref<string | null>(null);
const kickDurationPreset = ref<KickDurationPreset>('0');
const renameRoomDraft = ref('');
const renameRoomError = ref<string | null>(null);
const actionPending = ref(false);
const moveRoomSubmenuOpen = ref(false);
const now = ref(Date.now());
const participantDetail = ref<ParticipantDetail | null>(null);
const participantDetailError = ref<string | null>(null);
let nowHandle: number | null = null;
let participantDetailRequestId = 0;

const convexClient = useConvexClient();
const { mutate: publishConfetti } = useConvexMutation(api.celebrations.publishConfetti);
const { mutate: kickParticipant } = useConvexMutation(api.participants.kickParticipant);
const { mutate: moveParticipantsToRoom } = useConvexMutation(api.participants.moveParticipantsToRoom);
const { mutate: renameRoom } = useConvexMutation(api.rooms.renameRoom);
const { mutate: selectRandomStudent } = useConvexMutation(api.selections.selectRandomStudent);
const { mutate: sendParticipantToHallway } = useConvexMutation(api.participants.sendParticipantToHallway);
const { mutate: setHandRaised } = useConvexMutation(api.participants.setHandRaised);
const { mutate: setParticipantPermission } = useConvexMutation(api.permissions.setParticipantPermission);

const viewerIsTeacher = computed(() => props.viewerRole === 'teacher');
const selectedParticipantNames = computed(() => selectedParticipants.value.map((participant) => participant.displayName));
const moderatorActionParticipants = computed(() =>
  selectedParticipants.value.filter((participant) => participant.id !== props.viewerId && participant.role !== 'teacher'),
);
const moveRoomOptions = computed(() => {
  if (!viewerIsTeacher.value || moderatorActionParticipants.value.length === 0) {
    return [];
  }

  return props.rooms.filter((room) => {
    if (!room.isOpen) {
      return false;
    }

    return !moderatorActionParticipants.value.every((participant) => participant.apparentRoom.slug === room.slug);
  });
});
const canCommitRenameRoom = computed(() => {
  const nextName = renameRoomDraft.value.trim();
  return Boolean(nextName) && !actionPending.value && nextName !== renameRoomTarget.value?.displayName;
});
const kickDurationOptions: Array<{ description: string; label: string; value: KickDurationPreset }> = [
  {
    description: 'Show the kicked screen, but allow an immediate manual rejoin.',
    label: '0 min',
    value: '0',
  },
  {
    description: 'Require a one-minute cooldown before manual rejoin.',
    label: '1 min',
    value: '1',
  },
  {
    description: 'Require a five-minute cooldown before manual rejoin.',
    label: '5 min',
    value: '5',
  },
  {
    description: 'Require a ten-minute cooldown before manual rejoin.',
    label: '10 min',
    value: '10',
  },
  {
    description: 'Set a custom cooldown in whole minutes.',
    label: 'Custom',
    value: 'custom',
  },
];
const kickDialogNames = computed(() => kickDialogParticipants.value.map((participant) => participant.displayName).join(', '));
const resolvedKickDurationMs = computed(() => {
  switch (kickDurationPreset.value) {
    case '0':
      return 0;
    case '1':
      return 60_000;
    case '5':
      return 5 * 60_000;
    case '10':
      return 10 * 60_000;
    default:
      return parseCustomKickDurationMs(customKickDurationMinutes.value);
  }
});
const kickDurationError = computed(() => {
  if (kickDurationPreset.value !== 'custom') {
    return null;
  }

  if (customKickDurationMinutes.value === null || customKickDurationMinutes.value.trim() === '') {
    return 'Enter a custom cooldown in whole minutes.';
  }

  if (resolvedKickDurationMs.value === null) {
    return 'Custom cooldowns must be non-negative whole minutes.';
  }

  return null;
});
const canCommitKick = computed(() => {
  return kickDialogParticipants.value.length > 0 && resolvedKickDurationMs.value !== null && !actionPending.value;
});
const permissionItems = [
  { icon: '🎤', key: 'audio', label: getPermissionLabel('audio') },
  { icon: '📹', key: 'video', label: getPermissionLabel('video') },
  { icon: '💬', key: 'chat', label: getPermissionLabel('chat') },
  { icon: '🖥', key: 'screenshare', label: getPermissionLabel('screenshare') },
  { icon: '✏️', key: 'whiteboard', label: getPermissionLabel('whiteboard') },
  { icon: '👑', key: 'topMenuControl', label: getPermissionLabel('topMenuControl') },
] as const;

const selectedParticipants = computed(() => {
  if (!props.menuState || props.menuState.kind !== 'participant') {
    return [];
  }

  return props.menuState.participants;
});

const participantActions = computed(() => {
  if (!props.menuState || props.menuState.kind !== 'participant') {
    return [];
  }

  const actions: Array<{ disabled?: boolean; icon: string; label: string; run: () => void | Promise<void> }> = [];
  const participant = props.menuState.participant;

  if (viewerIsTeacher.value) {
    actions.push({ icon: '✋', label: 'Lower Hand', run: () => void lowerHand() });
    actions.push({
      disabled: moderatorActionParticipants.value.length === 0,
      icon: '🚫',
      label: 'Kick User',
      run: () => openKickDialog(),
    });
    actions.push({
      disabled: moderatorActionParticipants.value.length === 0,
      icon: '🚪',
      label: 'Send to Hallway',
      run: () => void moveParticipantsToHallway(),
    });
    actions.push({ disabled: true, icon: '📝', label: 'Reset User Whiteboard', run: () => undefined });
    actions.push({ icon: '🎉', label: 'Confetti', run: () => void launchConfetti() });
  }

  if (participant.id !== props.viewerId && (viewerIsTeacher.value || props.chatEnabled)) {
    actions.push({ icon: '✉️', label: 'Private Message User', run: () => openDirectThread() });
  } else if (!viewerIsTeacher.value) {
    actions.push({ disabled: true, icon: '📝', label: 'Reset My Whiteboard', run: () => undefined });
  }

  return actions;
});

const participantInfoRows = computed(() => {
  if (!props.menuState || props.menuState.kind !== 'participant' || !viewerIsTeacher.value) {
    return [];
  }

  const participant = props.menuState.participant;
  const awayTime = participantDetail.value
    ? participantDetail.value.awayTime +
      (participantDetail.value.awayTimeStamp > 0 ? now.value - participantDetail.value.awayTimeStamp : 0)
    : null;
  const browserLabel = participantDetailError.value
    ? 'Browser: Unavailable'
    : participantDetail.value
      ? `Browser: ${participantDetail.value.browserName}${participantDetail.value.browserVersion ? ` ${participantDetail.value.browserVersion}` : ''}`
      : 'Browser: Loading...';

  return [
    { icon: '⏳', label: awayTime === null ? 'Time Away: Loading...' : `Time Away: ${formatElapsed(awayTime)}` },
    {
      icon: '⏱',
      label:
        awayTime === null ? 'Time Absent: Loading...' : `Time Absent: ${participant.isKicked ? formatElapsed(awayTime) : '0:00'}`,
    },
    {
      icon: '🚫',
      label: participantDetail.value ? `Times Kicked: ${participantDetail.value.kickedCount}` : 'Times Kicked: Loading...',
    },
    { icon: '🆔', label: `User ID: ${participant.id}` },
    { icon: '👤', label: `Full Name: ${participant.displayName}` },
    { icon: '⚙️', label: browserLabel },
  ];
});

const roomActions = computed(() => {
  if (!props.menuState || props.menuState.kind !== 'room' || !viewerIsTeacher.value) {
    return [];
  }

  const actions: Array<{ disabled?: boolean; icon: string; label: string; run: () => void | Promise<void> }> = [];

  if (props.menuState.room.kind !== 'main') {
    actions.push({ icon: '✏️', label: 'Rename Room', run: () => openRenameDialog() });
  }

  if (props.menuState.room.participants.some((participant) => participant.role === 'student')) {
    actions.push({ icon: '🎲', label: 'Select Random Student', run: () => void runRandomSelection() });
  }

  return actions;
});

watch(
  () => props.menuState,
  async (menuState) => {
    moveRoomSubmenuOpen.value = false;

    if (!menuState) {
      return;
    }

    await nextTick();
    updatePanelPosition(menuState.x, menuState.y);
  },
);

watch(
  () => (props.menuState?.kind === 'participant' && viewerIsTeacher.value ? props.menuState.participant.id : null),
  async (participantId) => {
    participantDetailRequestId += 1;
    const requestId = participantDetailRequestId;
    participantDetail.value = null;
    participantDetailError.value = null;

    if (!participantId) {
      return;
    }

    try {
      const detail = await convexClient.query(api.participants.getParticipantDetail, {
        conferenceSlug: props.conferenceSlug,
        participantId,
      });

      if (requestId !== participantDetailRequestId) {
        return;
      }

      participantDetail.value = (detail as ParticipantDetail | null) ?? null;
    } catch (error) {
      if (requestId !== participantDetailRequestId) {
        return;
      }

      participantDetailError.value = error instanceof Error ? error.message : 'Unable to load participant details.';
    }
  },
  { immediate: true },
);

watch(
  () => moveRoomSubmenuOpen.value,
  async (isOpen) => {
    if (!isOpen) {
      return;
    }

    await nextTick();
    updateMoveRoomSubmenuPosition();
  },
);

watch(
  () => props.menuState,
  (menuState) => {
    if (!menuState) {
      stopClock();
      return;
    }

    if (menuState.kind === 'participant' && viewerIsTeacher.value) {
      startClock();
    } else {
      stopClock();
    }
  },
  { immediate: true },
);

watch(showRenameRoomDialog, (isOpen) => {
  if (isOpen) {
    return;
  }

  renameRoomDraft.value = '';
  renameRoomError.value = null;
  renameRoomTarget.value = null;
});

watch(renameRoomDraft, () => {
  renameRoomError.value = null;
});

watch(showKickDialog, (isOpen) => {
  if (isOpen) {
    return;
  }

  customKickDurationMinutes.value = null;
  kickDialogParticipants.value = [];
  kickDurationPreset.value = '0';
});

onMounted(() => {
  document.addEventListener('mousedown', handlePointerDown);
  document.addEventListener('keydown', handleKeyDown);
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handlePointerDown);
  document.removeEventListener('keydown', handleKeyDown);
  stopClock();
});

function startClock(): void {
  stopClock();
  now.value = Date.now();
  nowHandle = window.setInterval(() => {
    now.value = Date.now();
  }, 1000);
}

function stopClock(): void {
  if (nowHandle !== null) {
    window.clearInterval(nowHandle);
    nowHandle = null;
  }
}

function closeMenu(): void {
  moveRoomSubmenuOpen.value = false;
  emit('close');
}

function handlePointerDown(event: MouseEvent): void {
  if (!props.menuState) {
    return;
  }

  const target = event.target as Node;

  if (!panel.value?.contains(target) && !submenuPanel.value?.contains(target)) {
    closeMenu();
  }
}

function handleKeyDown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && moveRoomSubmenuOpen.value) {
    moveRoomSubmenuOpen.value = false;
    return;
  }

  if (event.key === 'Escape' && props.menuState) {
    closeMenu();
  }
}

function updatePanelPosition(x: number, y: number): void {
  if (!panel.value) {
    return;
  }

  const rect = panel.value.getBoundingClientRect();
  const maxLeft = window.innerWidth - rect.width - 20;
  const maxTop = window.innerHeight - rect.height - 20;

  panelStyle.value = {
    left: `${Math.max(20, Math.min(x, maxLeft))}px`,
    top: `${Math.max(20, Math.min(y, maxTop))}px`,
  };
}

function updateMoveRoomSubmenuPosition(): void {
  if (!moveRoomTrigger.value || !submenuPanel.value) {
    return;
  }

  const triggerRect = moveRoomTrigger.value.getBoundingClientRect();
  const submenuRect = submenuPanel.value.getBoundingClientRect();
  const canOpenRight = triggerRect.right + submenuRect.width + 8 <= window.innerWidth - 12;
  const left = canOpenRight ? triggerRect.right + 4 : triggerRect.left - submenuRect.width - 4;
  const maxTop = window.innerHeight - submenuRect.height - 20;

  submenuStyle.value = {
    left: `${Math.max(12, left)}px`,
    top: `${Math.max(20, Math.min(triggerRect.top - 4, maxTop))}px`,
  };
}

function openMoveRoomSubmenu(): void {
  if (actionPending.value || moveRoomOptions.value.length === 0) {
    return;
  }

  moveRoomSubmenuOpen.value = true;
}

function formatElapsed(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

async function runMenuAction(action: () => Promise<void>): Promise<void> {
  if (actionPending.value) {
    return;
  }

  actionPending.value = true;

  try {
    await action();
  } catch (error) {
    emit('notice', {
      detail: error instanceof Error ? error.message : 'The selected action could not be completed.',
      label: 'Context menu action failed',
    });
  } finally {
    actionPending.value = false;
  }
}

async function setPermission(permission: (typeof permissionItems)[number]['key'], value: boolean): Promise<void> {
  await runMenuAction(async () => {
    await Promise.all(
      selectedParticipants.value.map((participant) =>
        setParticipantPermission({
          conferenceSlug: props.conferenceSlug,
          participantId: participant.id,
          permission,
          value,
        }),
      ),
    );

    emit('notice', {
      detail: `${value ? 'Enabled' : 'Disabled'} ${getPermissionLabel(permission)} for ${selectedParticipantNames.value.join(', ')}.`,
      label: 'Participant permission updated',
    });
    closeMenu();
  });
}

async function lowerHand(): Promise<void> {
  await runMenuAction(async () => {
    await Promise.all(
      selectedParticipants.value.map((participant) =>
        setHandRaised({
          conferenceSlug: props.conferenceSlug,
          participantId: participant.id,
          value: false,
        }),
      ),
    );

    emit('notice', {
      detail: selectedParticipantNames.value.join(', '),
      label: 'Hands lowered',
    });
    closeMenu();
  });
}

function openKickDialog(): void {
  if (moderatorActionParticipants.value.length === 0) {
    return;
  }

  kickDialogParticipants.value = [...moderatorActionParticipants.value];
  showKickDialog.value = true;
  closeMenu();
}

async function commitKick(closeDialog: () => void): Promise<void> {
  const kickDurationMs = resolvedKickDurationMs.value;
  const participants = [...kickDialogParticipants.value];

  if (participants.length === 0 || kickDurationMs === null) {
    return;
  }

  const names = participants.map((participant) => participant.displayName).join(', ');

  await runMenuAction(async () => {
    await Promise.all(
      participants.map((participant) =>
        kickParticipant({
          conferenceSlug: props.conferenceSlug,
          kickDurationMs,
          participantId: participant.id,
        }),
      ),
    );

    emit('notice', {
      detail:
        kickDurationMs === 0
          ? `${names} can rejoin immediately.`
          : `${names} can rejoin in ${formatKickDuration(kickDurationMs)}.`,
      label: 'Participant kicked',
    });
    closeDialog();
  });
}

async function moveParticipantsToHallway(): Promise<void> {
  if (moderatorActionParticipants.value.length === 0) {
    return;
  }

  await runMenuAction(async () => {
    await Promise.all(
      moderatorActionParticipants.value.map((participant) =>
        sendParticipantToHallway({
          conferenceSlug: props.conferenceSlug,
          participantId: participant.id,
        }),
      ),
    );

    emit('notice', {
      detail: moderatorActionParticipants.value.map((participant) => participant.displayName).join(', '),
      label: 'Sent to hallway',
    });
    closeMenu();
  });
}

async function moveSelectedParticipantsToRoom(roomSlug: string): Promise<void> {
  if (moderatorActionParticipants.value.length === 0) {
    return;
  }

  const targetRoom = props.rooms.find((room) => room.slug === roomSlug);

  if (!targetRoom) {
    return;
  }

  await runMenuAction(async () => {
    const result = await moveParticipantsToRoom({
      conferenceSlug: props.conferenceSlug,
      participantIds: moderatorActionParticipants.value.map((participant) => participant.id),
      roomSlug,
    });

    const movedNames = moderatorActionParticipants.value
      .filter((participant) => result.movedParticipantIds.includes(participant.id))
      .map((participant) => participant.displayName)
      .join(', ');

    emit('notice', {
      detail: movedNames
        ? `${movedNames} to ${result.roomDisplayName}`
        : `Selected participants are already in ${targetRoom.displayName}.`,
      label: movedNames ? 'Participants moved' : 'No room change needed',
    });
    moveRoomSubmenuOpen.value = false;
    closeMenu();
  });
}

async function launchConfetti(): Promise<void> {
  await runMenuAction(async () => {
    await Promise.all(
      selectedParticipants.value.map((participant) =>
        publishConfetti({
          conferenceSlug: props.conferenceSlug,
          targetParticipantId: participant.id,
        }),
      ),
    );

    emit('notice', {
      detail: selectedParticipantNames.value.join(', '),
      label: 'Confetti launched',
    });
    closeMenu();
  });
}

function openDirectThread(): void {
  if (!props.menuState || props.menuState.kind !== 'participant') {
    return;
  }

  emit('open-thread', props.menuState.participant.id);
  emit('notice', {
    detail: props.menuState.participant.displayName,
    label: 'Direct chat selected',
  });
  closeMenu();
}

function openRenameDialog(): void {
  if (!props.menuState || props.menuState.kind !== 'room') {
    return;
  }

  renameRoomTarget.value = {
    displayName: props.menuState.room.displayName,
    slug: props.menuState.room.slug,
  };
  renameRoomDraft.value = props.menuState.room.displayName;
  renameRoomError.value = null;
  showRenameRoomDialog.value = true;
  closeMenu();
}

async function commitRenameRoom(closeDialog: () => void): Promise<void> {
  if (!renameRoomTarget.value) {
    return;
  }

  const nextName = renameRoomDraft.value.trim();

  if (!nextName || nextName === renameRoomTarget.value.displayName) {
    return;
  }

  if (actionPending.value) {
    return;
  }

  actionPending.value = true;
  renameRoomError.value = null;

  try {
    await renameRoom({
      conferenceSlug: props.conferenceSlug,
      roomSlug: renameRoomTarget.value!.slug,
      value: nextName,
    });

    emit('notice', {
      detail: nextName,
      label: 'Room renamed',
    });
    closeDialog();
  } catch (error) {
    renameRoomError.value = error instanceof Error ? error.message : 'Unable to rename this room.';
  } finally {
    actionPending.value = false;
  }
}

async function runRandomSelection(): Promise<void> {
  if (!props.menuState || props.menuState.kind !== 'room') {
    return;
  }

  const room = props.menuState.room;

  await runMenuAction(async () => {
    await selectRandomStudent({
      conferenceSlug: props.conferenceSlug,
      roomSlug: room.slug,
    });

    emit('notice', {
      detail: room.displayName,
      label: 'Random student selected',
    });
    closeMenu();
  });
}

function parseCustomKickDurationMs(value: string | null): number | null {
  if (value === null) {
    return null;
  }

  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return null;
  }

  const minutes = Number(trimmedValue);

  if (!Number.isInteger(minutes) || minutes < 0) {
    return null;
  }

  return minutes * 60_000;
}

function formatKickDuration(kickDurationMs: number): string {
  const minutes = Math.floor(kickDurationMs / 60_000);
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}
</script>
